import { ComponentType, useEffect, useRef } from 'react';
import { Outlet, Navigate, useLocation, matchPath, useParams } from 'react-router-dom';
import { z } from 'zod';

import { captureObservabilityError } from '../observability';
import { useQueryParam } from '../utils/use-query-param';

import { useAuthentication } from './auth-provider';
import { normaliseUsername } from './auth-utils';
import { AuthenticationResponse, RegistrationMethod } from './types';

const HostedLogin = () => {
  const { initiateHostedLogin, isLoading, isAuthenticated } = useAuthentication();
  const redirect = useQueryParam('redirect');

  const initiated = useRef(false);

  useEffect(() => {
    const initiateHostedLoginWithErrorHandling = async () => {
      try {
        await initiateHostedLogin(redirect);
      } catch (err) {
        captureObservabilityError(err);
      }
    };

    if (!isLoading && !isAuthenticated && !initiated.current) {
      initiated.current = true;
      initiateHostedLoginWithErrorHandling();
    }
  }, [isLoading, isAuthenticated, initiateHostedLogin, redirect]);

  return null;
};

type RegisterProps = {
  onSubmit: (registerArgs: { username: string; password: string; registrationMethod: RegistrationMethod }) => void;
  passwordLess?: boolean;
};
type RegisterComponentProps = {
  readonly apiUrl: string;
  readonly getAuthSessionContext?: () => object;
  readonly redirect?: string;
  readonly passwordLess?: boolean;
  readonly defaultMethod?: RegistrationMethod;
  readonly allowToggle?: boolean;
};

type RegisterOptions = {
  path?: string;
  passwordLess?: boolean;
  defaultMethod?: RegistrationMethod;
  allowToggle?: boolean;
};
const defaultOptions: RegisterOptions = {};
const withRegisterSubmit =
  <T extends RegisterProps>(
    Component: ComponentType<T>,
    { passwordLess, defaultMethod, allowToggle } = defaultOptions
  ) =>
  (props: Omit<RegisterComponentProps & T, keyof RegisterProps>) => {
    const { apiUrl, getAuthSessionContext } = props;
    const { postLogin } = useAuthentication();
    const { organisation } = useParams();
    const redirectParam = useQueryParam('redirect');
    const redirect = props.redirect ?? redirectParam;

    const onSubmit = async ({
      username,
      password,
      registrationMethod,
    }: {
      username: string;
      password: string;
      registrationMethod: RegistrationMethod;
    }) => {
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          passwordLess: Boolean(passwordLess),
          registrationMethod,
          username,
          ...(organisation && { organisation }),
          context: getAuthSessionContext ? getAuthSessionContext() : {},
        }),
      });

      const responsePayload = await handleResponse(response, 'REGISTRATION_FAILED');

      postLogin(responsePayload, redirect);
    };

    return (
      <Component
        {...(props as T)}
        allowToggle={allowToggle}
        defaultMethod={defaultMethod}
        onSubmit={onSubmit}
        passwordLess={passwordLess}
      />
    );
  };

type LoginProps = {
  onSubmit: ({ username, password }: { username: string; password: string }) => void;
  passwordLess?: boolean;
};
type LoginComponentProps = {
  readonly apiUrl: string;
  readonly getAuthSessionContext?: () => object;
  readonly onMfaChallenge?: (challenge: { session: string; username: string; organisation?: string }) => void;
};

const mfaChallengeResponseSchema = z.object({
  mfaRequired: z.literal(true),
  session: z.string().min(1),
  user: z.string().min(1),
  organisation: z.string().optional(),
});

type LoginOptions = {
  passwordLess?: boolean;
};
const defaultLoginOptions: LoginOptions = {};
const withLoginSubmit =
  <T extends LoginProps>(Component: ComponentType<T>, { passwordLess } = defaultLoginOptions) =>
  (props: Omit<LoginComponentProps & T, keyof LoginProps>) => {
    const { apiUrl, getAuthSessionContext, onMfaChallenge } = props;
    const { postLogin } = useAuthentication();
    const { organisation } = useParams();
    const redirect = useQueryParam('redirect');

    const onSubmit = async ({ username, password }: { username: string; password: string }) => {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: normaliseUsername(username),
          password,
          ...(organisation && { organisation }),
          context: getAuthSessionContext ? getAuthSessionContext() : {},
        }),
      });

      if (onMfaChallenge && response.ok) {
        const payload = await response.clone().json();
        const result = mfaChallengeResponseSchema.safeParse(payload);

        if (result.success) {
          onMfaChallenge({
            organisation: result.data.organisation,
            session: result.data.session,
            username: result.data.user,
          });
          return;
        }
      }

      const responsePayload = await handleResponse(response, 'login failed');

      postLogin(responsePayload, redirect);
    };

    return <Component {...(props as T)} onSubmit={onSubmit} passwordLess={passwordLess} />;
  };

type ChangePasswordProps = {
  onSubmit: ({
    currentPassword,
    newPassword,
    repeatPassword,
  }: {
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
  }) => void;
};

type ChangePasswordComponentProps = {
  readonly apiUrl: string;
};

const withChangePasswordSubmit =
  <T extends ChangePasswordProps>(Component: ComponentType<T>) =>
  (props: Omit<ChangePasswordComponentProps & T, keyof ChangePasswordProps>) => {
    const { apiUrl } = props;

    const onSubmit = async ({
      currentPassword,
      newPassword,
      repeatPassword,
    }: {
      currentPassword: string;
      newPassword: string;
      repeatPassword: string;
    }) => {
      if (newPassword !== repeatPassword) throw new Error('New passwords do not match');

      const response = await fetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      await handleResponse(response, 'Failed to change password');
    };

    return <Component {...(props as T)} onSubmit={onSubmit} />;
  };

const AuthCallback = () => {
  const { handleCallback, authError } = useAuthentication();
  const initiated = useRef(false);

  useEffect(() => {
    const handleCallbackWithErrorHandling = async () => {
      try {
        await handleCallback();
      } catch (err) {
        captureObservabilityError(err);
      }
    };

    if (!initiated.current) {
      initiated.current = true;
      handleCallbackWithErrorHandling();
    }
  }, [handleCallback]);

  if (authError) return <div>Authentication failed: {authError}</div>;

  return <div>Completing authentication...</div>;
};

type RequireAuthProps = {
  readonly whitelistedPaths?: string[];
  readonly getLoginRoute: (config: { organisation?: string }) => string | null;
};
const useRedirect = () => {
  const { pathname, search } = useLocation();
  return `${pathname}${search}`;
};
const RequireAuth = ({ whitelistedPaths, getLoginRoute }: RequireAuthProps) => {
  const { isAuthenticated, organisation, isLoading } = useAuthentication();
  const { pathname } = useLocation();
  const redirect = useRedirect();
  const isWhitelisted = whitelistedPaths?.some((path) => matchPath({ path }, pathname));

  if (!isWhitelisted && !isAuthenticated && !isLoading) {
    const loginRoute = getLoginRoute({ organisation });
    if (loginRoute === null) return null;

    return <Navigate to={`${loginRoute}?redirect=${redirect}`} />;
  }

  return <Outlet />;
};

const RequireNoAuth = ({ getIndexRoute }: { readonly getIndexRoute: () => string }) => {
  const { isAuthenticated, isLoading } = useAuthentication();

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={getIndexRoute()} />;

  return <Outlet />;
};

const handleResponse = async (response: Response, defaultError: string): Promise<AuthenticationResponse> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message);
  }

  const responsePayload: AuthenticationResponse = await response.json();

  if (!responsePayload.success) throw new Error(defaultError);

  return responsePayload;
};

export {
  AuthCallback,
  HostedLogin,
  RequireAuth,
  RequireNoAuth,
  withChangePasswordSubmit,
  withLoginSubmit,
  withRegisterSubmit,
};
